import React, { useState, useEffect } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { numberFormat } from 'helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faCommenting,
  faStarHalfStroke,
  faVolumeControlPhone,
  faPeopleGroup,
  faPeopleArrows,
  faPhoneVolume
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchHVACallingTodayKPIs,
  fetchHVACallingTodayKPIsInbound,
  FetchHVACallingTodayKPIsParams
} from 'services/hvacallingmanagment.service';

interface CrmStatCardProps {
  campaignid: number;
  campaignType?: 'outbound' | 'inbound';
}

interface OutboundKPIs {
  actualcalls: number;
  clients: number;
  dialed: number;
  answer: number;
  talktime: number;
  allcalls: number;
}

interface InboundKPIs {
  offered: number;
  handled: number;
  talktime: number;
  allcalls: number;
}

type HVACallingKPIs = OutboundKPIs | InboundKPIs;

// Type guards
const isOutboundKPIs = (kpis: HVACallingKPIs): kpis is OutboundKPIs => {
  return 'clients' in kpis;
};

const isInboundKPIs = (kpis: HVACallingKPIs): kpis is InboundKPIs => {
  return 'offered' in kpis;
};

const HVASystemKPICard3 = ({
  campaignid,
  campaignType = 'outbound'
}: CrmStatCardProps) => {
  const [kpis, setKpis] = useState<HVACallingKPIs>(() => {
    if (campaignType === 'inbound') {
      return {
        offered: 0,
        handled: 0,
        talktime: 0,
        allcalls: 0
      };
    }
    return {
      actualcalls: 0,
      clients: 0,
      dialed: 0,
      answer: 0,
      talktime: 0,
      allcalls: 0
    };
  });

  useEffect(() => {
    const getKPIs = async () => {
      try {
        const params: FetchHVACallingTodayKPIsParams = {
          campaign: campaignid
        };

        const response =
          campaignType === 'inbound'
            ? await fetchHVACallingTodayKPIsInbound(params)
            : await fetchHVACallingTodayKPIs(params);

        setKpis(response);
      } catch (error) {
        console.error('Error fetching KPIs:', error);
      }
    };

    getKPIs();
    const intervalId = setInterval(getKPIs, 10000);
    return () => clearInterval(intervalId);
  }, [campaignid, campaignType]);

  // Funciones auxiliares
  const safeDivide = (numerator: number, denominator: number) =>
    denominator !== 0 ? numerator / denominator : 0;

  const formattedTalkTime = (() => {
    const value = kpis.talktime / 3600;
    if (value == null) return '0h:0m';
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours}h:${minutes}m`;
  })();

  // Calcular avg talk time según el tipo
  const getAvgTalkTime = () => {
    let totalSeconds = 0;

    if (isOutboundKPIs(kpis)) {
      totalSeconds =
        kpis.answer > 0 ? safeDivide(kpis.talktime, kpis.answer) : 0;
    } else if (isInboundKPIs(kpis)) {
      totalSeconds =
        kpis.handled > 0 ? safeDivide(kpis.talktime, kpis.handled) : 0;
    }

    // Verificar que el resultado sea válido
    if (!isFinite(totalSeconds) || isNaN(totalSeconds) || totalSeconds === 0) {
      return '0m:0s';
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}m:${seconds}s`;
  };

  // Renderizado para Outbound
  const renderOutboundKPIs = (kpis: OutboundKPIs) => (
    <>
      {/* Saturation */}
      <Row className="mx-0 mt-0 pt-1 border-bottom bg-body">
        <Col className="fs-9 text-start p-0 px-2 pt-1 fw-bold" xs={6}>
          <FontAwesomeIcon
            icon={faStarHalfStroke}
            className="text-primary me-1"
          />
          Saturation
        </Col>
        <Col className="fs-8 fw-bold text-end p-0 px-2" xs={6}>
          {safeDivide(kpis.allcalls, kpis.clients).toFixed(2)}
        </Col>
      </Row>

      {/* Clients */}
      <Row className="mx-0 mt-1">
        <Col className="fs-9 text-start p-0 px-2" xs={5}>
          <FontAwesomeIcon icon={faPeopleGroup} className="text-primary me-1" />
          Clients
        </Col>
        <Col className="fs-9 fw-bold text-end p-0 px-2" xs={7}>
          {numberFormat(kpis.clients)}
        </Col>
      </Row>

      {/* Total calls */}
      <Row className="mx-0 mt-0">
        <Col className="fs-9 text-start p-0 px-2" xs={5}>
          <FontAwesomeIcon
            icon={faVolumeControlPhone}
            className="text-primary me-2"
          />
          Total calls
        </Col>
        <Col className="fs-9 fw-bold text-end p-0 px-2" xs={7}>
          {numberFormat(kpis.allcalls)}
        </Col>
      </Row>
    </>
  );

  // Renderizado para Inbound
  const renderInboundKPIs = (kpis: InboundKPIs) => (
    <>
      {/* Handle Rate */}
      <Row className="mx-0 mt-0 pt-1 border-bottom bg-body">
        <Col className="fs-9 text-start p-0 px-2 pt-1 fw-bold" xs={6}>
          <FontAwesomeIcon
            icon={faPeopleArrows}
            className="text-primary me-1"
          />
          Handle rate
        </Col>
        <Col className="fs-8 fw-bold text-end p-0 px-2" xs={6}>
          {kpis.offered > 0
            ? `${Math.round((kpis.handled / kpis.offered) * 100)}%`
            : '0%'}
        </Col>
      </Row>

      {/* Offered */}
      <Row className="mx-0 mt-1">
        <Col className="fs-9 text-start p-0 px-2" xs={5}>
          <FontAwesomeIcon icon={faPhoneVolume} className="text-primary me-1" />
          Offered
        </Col>
        <Col className="fs-9 fw-bold text-end p-0 px-2" xs={7}>
          {numberFormat(kpis.offered)}
        </Col>
      </Row>

      {/* Handled */}
      <Row className="mx-0 mt-0">
        <Col className="fs-9 text-start p-0 px-2" xs={5}>
          <FontAwesomeIcon
            icon={faPeopleArrows}
            className="text-primary me-1"
          />
          Handled
        </Col>
        <Col className="fs-9 fw-bold text-end p-0 px-2" xs={7}>
          {numberFormat(kpis.handled)}
        </Col>
      </Row>
    </>
  );

  // Renderizado de KPIs comunes
  const renderCommonKPIs = () => (
    <>
      {/* Talk time */}
      <Row className="mx-0 mt-0">
        <Col className="fs-9 text-start p-0 px-2" xs={5}>
          <FontAwesomeIcon icon={faComments} className="text-primary me-1" />
          Talk time
        </Col>
        <Col className="fs-9 fw-bold text-end p-0 px-2" xs={7}>
          {formattedTalkTime}
        </Col>
      </Row>

      {/* Avg. talk time */}
      <Row className="mx-0 mt-0 mb-0">
        <Col className="fs-9 text-start p-0 px-2" xs={7}>
          <FontAwesomeIcon icon={faCommenting} className="text-primary me-1" />
          Avg. talk time
        </Col>
        <Col className="fs-9 fw-bold p-0 text-end px-2" xs={5}>
          {getAvgTalkTime()}
        </Col>
      </Row>
    </>
  );

  // Renderizado principal
  return (
    <Card>
      <Card.Body className="p-0 mx-0">
        {isInboundKPIs(kpis)
          ? renderInboundKPIs(kpis)
          : isOutboundKPIs(kpis)
          ? renderOutboundKPIs(kpis)
          : null}
        {renderCommonKPIs()}
      </Card.Body>
    </Card>
  );
};

export default HVASystemKPICard3;
